import React, { useState, useMemo, useCallback } from 'react';
import { Button, Modal, Form, Input, Select, message, Space, Typography } from 'antd';
import { DataTable, PageLayout } from '../../ui';
import { useGetUsersQuery, useCreateUserMutation, useUpdateUserMutation, useDeactivateUserMutation, useActivateUserMutation, useResetUserPasswordMutation, useGetRolesListQuery } from '../../store/api/usersApi';
import type { User, Role } from '../../types';
import useHasPermission from '../../hooks/useHasPermission';
import { CopyOutlined } from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

const UsersPage: React.FC = () => {
  const { data: users = [], isLoading } = useGetUsersQuery();
  const { data: roles = [] } = useGetRolesListQuery();
  const [createUser] = useCreateUserMutation();
  const [updateUser] = useUpdateUserMutation();
  const [deactivateUser] = useDeactivateUserMutation();
  const [activateUser] = useActivateUserMutation();
  const [resetUserPassword] = useResetUserPasswordMutation();
  const canCreateUsers = useHasPermission('users.create');
  const canEditUsers = useHasPermission('users.edit');
  const canDeactivateUsers = useHasPermission('users.deactivate');

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [form] = Form.useForm();
  const [isPasswordResetModalVisible, setIsPasswordResetModalVisible] = useState(false);
  const [passwordResetUser, setPasswordResetUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState<string | null>(null);
  const [passwordForm] = Form.useForm();

  const openCreate = () => {
    if (!canCreateUsers) return message.error('Нет прав');
    setEditingUser(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const openEdit = useCallback((user: User) => {
    if (!canEditUsers) return message.error('Нет прав');
    setEditingUser(user);
    form.setFieldsValue({
      email: user.email,
      roleId: user.roleId,
      firstName: user.firstName,
      lastName: user.lastName
    });
    setIsModalVisible(true);
  }, [canEditUsers, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingUser) {
        if (!canEditUsers) return message.error('Нет прав');
        await updateUser({ id: editingUser.id, body: values }).unwrap();
        message.success('Пользователь обновлён');
      } else {
        if (!canCreateUsers) return message.error('Нет прав');
        await createUser(values).unwrap();
        message.success('Пользователь создан');
      }
      setIsModalVisible(false);
      form.resetFields();
    } catch (e: any) {
      message.error(e?.data?.error || 'Ошибка');
    }
  };

  const handleResetPassword = useCallback(async (user: User) => {
    if (!canEditUsers) return message.error('Нет прав');
    setPasswordResetUser(user);
    passwordForm.resetFields();
    setIsPasswordResetModalVisible(true);
    setNewPassword(null);
  }, [canEditUsers, passwordForm]);

  const handleConfirmPasswordReset = async () => {
    if (!passwordResetUser) return;
    
    try {
      const values = await passwordForm.validateFields();
      const result = await resetUserPassword({ 
        id: passwordResetUser.id, 
        newPassword: values.newPassword || undefined 
      }).unwrap();
      
      const generatedPassword = result?.data?.newPassword;
      if (generatedPassword) {
        setNewPassword(generatedPassword);
      } else {
        message.success('Пароль успешно сброшен');
        setIsPasswordResetModalVisible(false);
        setPasswordResetUser(null);
        passwordForm.resetFields();
      }
    } catch (e: any) {
      message.error(e?.data?.error || 'Ошибка сброса пароля');
    }
  };

  const copyPasswordToClipboard = () => {
    if (newPassword) {
      navigator.clipboard.writeText(newPassword);
      message.success('Пароль скопирован в буфер обмена');
    }
  };

  const columns = useMemo(() => [
    { title: 'ID', dataIndex: 'id', key: 'id' },
    { title: 'Логин', dataIndex: 'username', key: 'username' },
    {
      title: 'Имя',
      key: 'displayName',
      render: (_: unknown, record: User) =>
        [record.firstName, record.lastName].filter(Boolean).join(' ') || '—'
    },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Роль', dataIndex: ['role', 'name'], key: 'role' },
    { title: 'Активность', dataIndex: 'isActive', key: 'isActive', render: (val: boolean) => val ? 'Да' : 'Нет' },
    { title: 'Действия', key: 'actions', render: (_: any, record: User) => (
      <Space>
        {canEditUsers && <Button size="small" onClick={() => openEdit(record)}>Ред.</Button>}
        {canEditUsers && <Button size="small" onClick={() => handleResetPassword(record)}>Сбросить пароль</Button>}
        {record.isActive ? (
          canDeactivateUsers ? <Button size="small" danger onClick={() => deactivateUser(record.id)}>Деактивировать</Button> : null
        ) : (
          canEditUsers ? <Button size="small" onClick={() => activateUser(record.id)}>Активировать</Button> : null
        )}
      </Space>
    )}
  ], [canEditUsers, canDeactivateUsers, deactivateUser, activateUser, openEdit, handleResetPassword]);

  return (
    <PageLayout
      title="Пользователи"
      subtitle="Учётные записи и роли доступа"
      actions={
        canCreateUsers ? (
          <Button type="primary" onClick={openCreate}>
            Создать пользователя
          </Button>
        ) : undefined
      }
    >
      <DataTable rowKey="id" loading={isLoading} dataSource={users} columns={columns} />

      <Modal open={isModalVisible} title={editingUser ? 'Редактировать пользователя' : 'Создать пользователя'} onCancel={() => setIsModalVisible(false)} onOk={handleOk}>
        <Form form={form} layout="vertical">
          <Form.Item name="username" label="Логин" rules={[{ required: !editingUser, message: 'Обязателен' }]}>
            <Input disabled={!!editingUser} />
          </Form.Item>
          <Form.Item name="firstName" label="Имя">
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Фамилия">
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            getValueFromEvent={(e: any) => (e && e.target ? String(e.target.value).trim() : e)}
            rules={[{ required: true, message: 'Пожалуйста, введите Email' }, { type: 'email', message: 'Неверный формат Email' }]}
          >
            <Input />
          </Form.Item>
          {!editingUser && (
            <Form.Item name="password" label="Пароль" rules={[{ required: true }]}> 
              <Input.Password />
            </Form.Item>
          )}
          <Form.Item name="roleId" label="Роль" rules={[{ required: true }]}> 
            <Select>
              {roles?.map((r: Role) => <Option key={r.id} value={r.id}>{r.name}</Option>)}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Модальное окно сброса пароля */}
      <Modal
        open={isPasswordResetModalVisible}
        title="Сбросить пароль"
        onCancel={() => {
          setIsPasswordResetModalVisible(false);
          setPasswordResetUser(null);
          setNewPassword(null);
          passwordForm.resetFields();
        }}
        footer={newPassword ? [
          <Button key="copy" icon={<CopyOutlined />} onClick={copyPasswordToClipboard}>
            Копировать пароль
          </Button>,
          <Button key="close" onClick={() => {
            setIsPasswordResetModalVisible(false);
            setPasswordResetUser(null);
            setNewPassword(null);
            passwordForm.resetFields();
          }}>
            Закрыть
          </Button>
        ] : [
          <Button key="cancel" onClick={() => {
            setIsPasswordResetModalVisible(false);
            setPasswordResetUser(null);
            setNewPassword(null);
            passwordForm.resetFields();
          }}>
            Отмена
          </Button>,
          <Button key="reset" type="primary" onClick={handleConfirmPasswordReset}>
            Сбросить пароль
          </Button>
        ]}
      >
        {newPassword ? (
          <div>
            <Text strong>Новый пароль для пользователя {passwordResetUser?.username}:</Text>
            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 16,
              wordBreak: 'break-all'
            }}>
              {newPassword}
            </div>
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              Сохраните этот пароль! Он больше не будет отображаться.
            </Text>
          </div>
        ) : (
          <Form form={passwordForm} layout="vertical">
            <Form.Item
              name="newPassword"
              label="Новый пароль (необязательно)"
              help="Если оставить пустым, будет сгенерирован случайный пароль"
            >
              <Input.Password placeholder="Оставьте пустым для автогенерации" />
            </Form.Item>
            <Text type="secondary">
              Пароль пользователя {passwordResetUser?.username} будет сброшен. 
              {!passwordForm.getFieldValue('newPassword') && ' Будет сгенерирован случайный пароль.'}
            </Text>
          </Form>
        )}
      </Modal>
    </PageLayout>
  );
};

export default UsersPage;


